import { Component, Output, Input, EventEmitter, OnInit, ViewEncapsulation, ViewChild, ElementRef } from '@angular/core';
import { Observable } from "rxjs";
import { BehaviorSubject } from "rxjs";
import { distinctUntilChanged, debounceTime } from 'rxjs/operators';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';

import { AutoSub, AutoSubOnDestroy } from '../../../providers';

@AutoSubOnDestroy()
@Component({
    selector: 'auto-complete',
    templateUrl: 'auto-complete.component.html',
    styleUrls: ['./auto-complete.component.scss'],
    encapsulation: ViewEncapsulation.None
})

export class AutoCompleteComponent implements OnInit {
    @ViewChild(CdkVirtualScrollViewport) scrollList: CdkVirtualScrollViewport;
    @ViewChild('SearchInput', { static: true }) searchInput: ElementRef;
    @Input() ITEM_HEIGHT = 70;

    // Placeholder to show when items are missing
    public ERR_IMAGE = "assets/images/placeholders/thumb.jpeg";
    // Height of items in list
    // List of all items to filter
    public itemList = [];
    // Filtered list (with custome view filter applied)
    public filteredList = [];
    // Last query from input field
    public lastQuery: string = "";
    // Subjects containing start and end search query streams
    private searchQuery: BehaviorSubject<string | null> = new BehaviorSubject("");
    private lastDeleteTime = Date.now();

    public highlightIdx: number = 0;
    public showList: boolean = false;
    // Indicates if the exact query item exists (in that case 'create new' option won't be shown) 
    public queryItemExists: boolean = false;

    // Event emitted when user select a item from search list
    @Output() itemSelected = new EventEmitter<any>();
    // Emitted when query is empty and backspace is pressed
    @Output() queryDeleted = new EventEmitter<void>();
    // Emitted when user clicks on create new option (with searchQuery)
    @Output() createItem = new EventEmitter<string>();

    // Filter callback function to be called when search changes
    @Input() filterFunction: (item: any, idx: number, searchString: string) => boolean = (item, idx, searchString) => true;
    // Function to be implemented by parent, returning true if the item already exists (determining if new item option should be shown)
    @Input() itemExistsFunction: (item: any, idx: number, searchString: string) => boolean = (item, idx, searchString) => false;
    // Optional object containing options such has what to display from items
    @Input() options: any = {};
    // Observable with items to show in search list
    @Input() itemObserver: Observable<Array<any>> = null;
    // Close search list when item is selected
    @Input() closeOnSelect: boolean = true;
    @Input() resetQueryOnSelect: boolean = true;

    // Show option to create new items if they do not exist
    @Input() showCreateOption: boolean = false;

    constructor() { }

    ngOnInit() {
        console.log("Auto subscribe to items now");
        this.subscribeToItems();
        // Subscribe to query change events
        AutoSub(this).reg['searchSub'] = this.searchQuery.pipe(debounceTime(90), distinctUntilChanged()).subscribe((query) => {
            // Apply filter on item list
            this.filter(query);
        });
    }

    private subscribeToItems() {
        // Subscribe to item observer
        AutoSub(this).reg['itemSub'] = this.itemObserver.subscribe((itemList: Array<any>) => {
            this.itemList = itemList;
            // Apply filter on returned item list
            this.filter(this.lastQuery);
        });
    }

    private filter(query: string) {
        if(query == "") query = "*";
        this.filteredList = this.itemList.filter((item, idx) => this.filterFunction(item, idx, query));
        // Check if item exists if create new is enabled
        if(this.showCreateOption && this.itemExistsFunction) this.queryItemExists = this.itemList.find((item, idx) => this.itemExistsFunction(item, idx, query));
    }

    public search(query): void {
        this.searchQuery.next(query);
    }

    public selectItem(item: any): void {
        this.itemSelected.emit(item);
        // Update list after item might have been removed
        this.filter(this.lastQuery);
        // Close after selecting item if this feature is enabled
        if(this.closeOnSelect) {
            this.showList = false;
            this.searchInput.nativeElement.blur();
        }

        if(this.resetQueryOnSelect) {
            this.searchInput.nativeElement.textContent = "";
            this.lastQuery = "";
        }
    }

    public handleLoadErr(imageRef) {
        console.log("Image error");
        imageRef = this.ERR_IMAGE;
    }


    public onInputBlur() {
        if(this.closeOnSelect) this.hideList();
    }

    public onTextInputChange(ev: Event) {
        let editable = ev.target as any as HTMLDivElement;
        this.lastQuery = editable.textContent;
        this.search(editable.textContent)
    }

    public onKeyDown(event: KeyboardEvent) {
        if(event.code == "ArrowDown") {
            // Show all
            if(this.lastQuery.length <= 0) this.filter("*");
            this.showList = true;

            this.highlightIdx++;
            if(this.highlightIdx >= this.filteredList.length) this.highlightIdx = this.filteredList.length - 1;

            if(!this.scrollList) return;
            // Scroll to item in list when highlight it hits bottom
            let scrollStartIdx = this.scrollList.measureScrollOffset() / this.ITEM_HEIGHT;
            if(this.highlightIdx > (scrollStartIdx + 3)) this.scrollList.scrollToIndex(this.highlightIdx - 3, "auto");
            event.preventDefault();
            return;
        }

        if(event.code == "ArrowUp") {
            // Show all
            if(this.lastQuery.length <= 0) this.filter("*");
            this.showList = true;

            this.highlightIdx--;
            // Clamp value to lower idx
            if(this.highlightIdx <= 0) this.highlightIdx = 0;

            if(!this.scrollList) return;
            // Scroll to item in list when highlight hits top
            let scrollStartIdx = this.scrollList.measureScrollOffset() / this.ITEM_HEIGHT;
            if(this.highlightIdx < scrollStartIdx) this.scrollList.scrollToIndex(this.highlightIdx, "auto");
            event.preventDefault();
            return;
        }

        if(event.code == "Enter") {
            // Select highlighted from list
            if(this.highlightIdx < 0 || this.highlightIdx >= this.filteredList.length) return;
            let item = this.filteredList[this.highlightIdx];
            this.selectItem(item);
            return;
        }

        // Emit delete event if backspace pressed and query is empty
        if(event.code == "Escape") {
            this.showList = false;
            return;
        }

        // Implement hysteresis in order not to delete too many elements on back press a once
        if(this.lastQuery.length <= 0 && Date.now() - this.lastDeleteTime < 2000) return;
        if(event.code == "Backspace") this.lastDeleteTime = Date.now();

        // Emit delete event if backspace pressed and query is empty
        if(event.code == "Backspace" && this.lastQuery.length <= 0) {
            this.showList = false;
            this.queryDeleted.emit();
            this.filter(this.lastQuery);
            return;
        }

        this.showList = true;
    }

    public hideList() {
        // Delay hide 30 ms in order to catch mouse press on selected element first
        setTimeout(() => { this.showList = false; }, 50);
    }

    public toggleOpen() {
        if(this.showList) {
            this.showList = false;
        }
        else {
            this.focusInput();
            this.showList = true;
        }
    }

    public focusInput() {
        this.searchInput.nativeElement.focus();
    }


    public itemProperty(item, propertyName) {
        return item[propertyName] || "none";
    }

    public onInputFocus() {
        this.filter(this.lastQuery ? this.lastQuery : "*");
    }

    public emitCreateItem() {
        this.createItem.emit(this.lastQuery);
    }
}
